import { useState, type FormEvent } from "react";
import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantMutation, useTenantQuery } from "../hooks/useTenantData";
import { can } from "../tenant/permissions";
import {
  OCCUPANCY_TYPE_LABELS,
  OCCUPANCY_VALIDITY_LABELS,
  occupancyValidity,
  type Occupancy,
  type OccupancyInput,
  type OccupancyType,
  type OccupancyValidity,
} from "../api/types";
import {
  Badge,
  ConfirmDialog,
  InlineError,
  PageHeader,
  Panel,
  QueryState,
} from "../components/ui";

const VALIDITY_TONES: Record<OccupancyValidity, "ok" | "danger" | "warn" | "muted"> = {
  active: "ok",
  expired: "danger",
  future: "warn",
  inactive: "muted",
};

export function OccupanciesPage() {
  const membership = useActiveTenant();
  const canEdit = can(membership.role, "occupancies.edit");
  const occupancies = useTenantQuery(["occupancies"], (api, t) => api.listOccupancies(t));
  const people = useTenantQuery(["people"], (api, t) => api.listPeople(t));
  const units = useTenantQuery(["units"], (api, t) => api.listUnits(t));

  const [validityFilter, setValidityFilter] = useState<"" | OccupancyValidity>("");
  const [removing, setRemoving] = useState<Occupancy | null>(null);

  const create = useTenantMutation<OccupancyInput>(
    (api, t, input) => api.createOccupancy(t, input),
    [["occupancies"], ["audit-events"]],
  );
  const remove = useTenantMutation<string>(
    (api, t, id) => api.deleteOccupancy(t, id),
    [["occupancies"], ["audit-events"]],
  );

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const validUntil = String(data.get("valid_until") || "");
    const temporary = data.get("is_temporary") === "on";
    create.mutate(
      {
        person_id: String(data.get("person_id") || ""),
        unit_id: String(data.get("unit_id") || ""),
        type: data.get("type") as OccupancyType,
        valid_from: new Date(String(data.get("valid_from") || "")).toISOString(),
        valid_until: validUntil ? new Date(validUntil).toISOString() : null,
        call_priority: Number(data.get("call_priority") || 1),
        can_receive_calls: data.get("can_receive_calls") === "on",
        can_activate_app: data.get("can_activate_app") === "on",
        is_primary: data.get("is_primary") === "on",
        is_temporary: temporary || Boolean(validUntil),
      },
      { onSuccess: () => form.reset() },
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <PageHeader
        title="Ocupações"
        subtitle="Vínculos entre pessoas e unidades: quem recebe chamadas e pode ativar o aplicativo."
      />
      {canEdit && (
        <Panel title="Novo vínculo">
          <form onSubmit={submit}>
            <div className="form-grid">
              <label>
                Pessoa
                <select name="person_id" required defaultValue="">
                  <option value="" disabled>Selecione</option>
                  {people.data?.filter((p) => p.status === "active").map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Unidade
                <select name="unit_id" required defaultValue="">
                  <option value="" disabled>Selecione</option>
                  {units.data?.filter((u) => u.active).map((u) => (
                    <option key={u.id} value={u.id}>{u.block_name} · {u.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Tipo de vínculo
                <select name="type" required defaultValue="resident">
                  {(Object.keys(OCCUPANCY_TYPE_LABELS) as OccupancyType[]).map((t) => (
                    <option key={t} value={t}>{OCCUPANCY_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </label>
              <label>
                Início da validade
                <input name="valid_from" type="date" required defaultValue={today} />
              </label>
              <label>
                Fim da validade (opcional)
                <input name="valid_until" type="date" />
              </label>
              <label>
                Prioridade de chamada
                <input name="call_priority" type="number" min={1} max={9} defaultValue={1} />
              </label>
            </div>
            <div className="form-grid" style={{ marginTop: 12 }}>
              <label className="checkbox">
                <input name="can_receive_calls" type="checkbox" defaultChecked />
                Pode receber chamadas
              </label>
              <label className="checkbox">
                <input name="can_activate_app" type="checkbox" defaultChecked />
                Pode ativar o aplicativo
              </label>
              <label className="checkbox">
                <input name="is_primary" type="checkbox" />
                Vínculo principal da unidade
              </label>
              <label className="checkbox">
                <input name="is_temporary" type="checkbox" />
                Vínculo temporário
              </label>
              <div className="form-actions">
                <button type="submit" disabled={create.isPending}>
                  {create.isPending ? "Salvando…" : "Criar vínculo"}
                </button>
              </div>
            </div>
          </form>
          <p>Vínculos com data final (locação, temporada, hóspede) expiram automaticamente e deixam de receber chamadas.</p>
          <InlineError error={create.error} />
        </Panel>
      )}
      <Panel title="Vínculos cadastrados">
        <div className="toolbar">
          <label>
            Situação
            <select value={validityFilter} onChange={(e) => setValidityFilter(e.target.value as "" | OccupancyValidity)}>
              <option value="">Todas</option>
              <option value="active">Vigentes</option>
              <option value="expired">Expirados</option>
              <option value="future">Futuros</option>
              <option value="inactive">Inativos</option>
            </select>
          </label>
        </div>
        <QueryState query={occupancies} empty="Nenhum vínculo cadastrado ainda.">
          {(items) => {
            const filtered = items.filter(
              (o) => !validityFilter || occupancyValidity(o) === validityFilter,
            );
            if (filtered.length === 0) return <p className="state state-empty">Nenhum vínculo com a situação selecionada.</p>;
            return (
              <div className="table">
                {filtered.map((occupancy) => {
                  const validity = occupancyValidity(occupancy);
                  return (
                    <div className="table-row" key={occupancy.id}>
                      <span>
                        <strong>{occupancy.person_name}</strong>
                        <small>
                          {OCCUPANCY_TYPE_LABELS[occupancy.type]} · {occupancy.block_name} · Unidade {occupancy.unit_label}
                          {occupancy.is_primary ? " · principal" : ""}
                        </small>
                        <small>
                          {occupancy.is_temporary ? "Temporário" : "Permanente"} · de {formatDate(occupancy.valid_from)}
                          {occupancy.valid_until ? ` até ${formatDate(occupancy.valid_until)}` : " (sem data final)"}
                          {` · prioridade ${occupancy.call_priority}`}
                          {occupancy.can_receive_calls ? " · recebe chamadas" : " · não recebe chamadas"}
                          {occupancy.can_activate_app ? " · ativa o app" : " · não ativa o app"}
                        </small>
                      </span>
                      <Badge tone={VALIDITY_TONES[validity]}>{OCCUPANCY_VALIDITY_LABELS[validity]}</Badge>
                      {canEdit && (
                        <span className="row-actions">
                          <button type="button" className="danger" onClick={() => setRemoving(occupancy)}>
                            Encerrar vínculo
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }}
        </QueryState>
        <InlineError error={remove.error} />
      </Panel>
      <ConfirmDialog
        open={removing !== null}
        title="Encerrar vínculo"
        description={
          removing
            ? `Encerrar o vínculo de ${removing.person_name} com a unidade ${removing.unit_label}? A pessoa deixa de receber chamadas desta unidade e os convites pendentes deixam de valer.`
            : ""
        }
        confirmLabel="Encerrar vínculo"
        busy={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) remove.mutate(removing.id, { onSettled: () => setRemoving(null) });
        }}
      />
    </>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR");
}
